import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check, MapPin, Phone, Shield, Star } from "lucide-react";
import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import MobileStickyCTA from "@/components/site/MobileStickyCTA";
import QuoteContact from "@/components/site/QuoteContact";
import Testimonials from "@/components/site/Testimonials";

/**
 * Reusable city landing page for local SEO.
 * Config drives H1, meta, JSON-LD, neighborhood callouts.
 */
export default function CityPage({ config }) {
  const {
    slug,
    city,
    county,
    zips,
    heroImage,
    heroKicker,
    neighborhoods,
    localCopy,
    galleryImages,
    services,
    faqs,
  } = config;

  const canonical = `https://mlexteriordesign.com/flooring-installation-${slug}`;

  useEffect(() => {
    const prevTitle = document.title;
    const prevDesc = document
      .querySelector('meta[name="description"]')
      ?.getAttribute("content");
    const prevCanonical = document
      .querySelector('link[rel="canonical"]')
      ?.getAttribute("href");

    document.title = `Flooring Installation ${city}, MO | M&L Design`;

    const desc = document.querySelector('meta[name="description"]');
    if (desc) {
      desc.setAttribute(
        "content",
        `Premium flooring installation in ${city}, Missouri — LVP, laminate, tile, flooring removal, and baseboards. Owner-operated, licensed & insured. Serving ${neighborhoods
          .slice(0, 4)
          .join(", ")} and every ${city} neighborhood. Free on-site quotes.`
      );
    }

    const canonicalEl = document.querySelector('link[rel="canonical"]');
    if (canonicalEl) canonicalEl.setAttribute("href", canonical);

    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "city-page-schema";
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "Service",
          "@id": `${canonical}#service`,
          serviceType: `Flooring Contractor · ${city}, MO`,
          name: `Flooring Installation in ${city}, Missouri`,
          provider: { "@id": "https://mlexteriordesign.com/#business" },
          areaServed: {
            "@type": "City",
            name: `${city}, Missouri`,
            containedInPlace: {
              "@type": "AdministrativeArea",
              name: county,
            },
          },
          hasOfferCatalog: {
            "@type": "OfferCatalog",
            name: `Flooring Services in ${city}`,
            itemListElement: services.map((s) => ({
              "@type": "Offer",
              itemOffered: { "@type": "Service", name: s.title },
            })),
          },
        },
        {
          "@type": "FAQPage",
          "@id": `${canonical}#faq`,
          mainEntity: faqs.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        },
        {
          "@type": "BreadcrumbList",
          itemListElement: [
            {
              "@type": "ListItem",
              position: 1,
              name: "Home",
              item: "https://mlexteriordesign.com/",
            },
            {
              "@type": "ListItem",
              position: 2,
              name: `Flooring Installation ${city}`,
              item: canonical,
            },
          ],
        },
      ],
    });
    document.head.appendChild(script);

    window.scrollTo(0, 0);

    return () => {
      document.title = prevTitle;
      if (desc && prevDesc) desc.setAttribute("content", prevDesc);
      if (canonicalEl && prevCanonical)
        canonicalEl.setAttribute("href", prevCanonical);
      const s = document.getElementById("city-page-schema");
      if (s) s.remove();
    };
  }, [slug]);

  return (
    <div
      className="min-h-screen bg-stone-50 text-stone-900"
      data-testid={`city-page-${slug}`}
    >
      <Header />

      <main>
        {/* HERO */}
        <section
          data-testid="city-hero"
          className="relative pt-32 pb-16 lg:pt-40 lg:pb-24 bg-stone-900 text-stone-50 overflow-hidden"
        >
          <div
            aria-hidden
            className="absolute inset-0 opacity-25"
            style={{
              backgroundImage: `url(${heroImage})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
            }}
          />
          <div
            aria-hidden
            className="absolute inset-0 bg-gradient-to-r from-stone-950 via-stone-950/80 to-stone-950/30"
          />

          <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10">
            <nav
              aria-label="Breadcrumb"
              className="text-xs text-stone-400 mb-6"
              data-testid="city-breadcrumb"
            >
              <Link to="/" className="hover:text-orange-400 transition-colors">
                Home
              </Link>{" "}
              <span className="text-stone-600">/</span>{" "}
              <Link
                to="/flooring-contractor-st-louis"
                className="hover:text-orange-400 transition-colors"
              >
                Flooring Contractor
              </Link>{" "}
              <span className="text-stone-600">/</span>{" "}
              <span className="text-stone-200">{city}, MO</span>
            </nav>

            <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.15em] text-orange-400 font-semibold mb-6">
              <MapPin size={13} />
              <span>{heroKicker}</span>
            </div>

            <h1
              data-testid="city-h1"
              className="font-display font-semibold text-4xl md:text-6xl lg:text-7xl leading-[1.02] tracking-[-0.03em] max-w-4xl"
            >
              Flooring Installation
              <br />
              <span className="text-orange-400">
                {city}, Missouri
              </span>
            </h1>

            <p className="mt-6 text-lg lg:text-xl text-stone-300 leading-relaxed max-w-2xl">
              Premium <strong className="text-stone-100">LVP</strong>,{" "}
              <strong className="text-stone-100">laminate</strong>, and{" "}
              <strong className="text-stone-100">tile installation</strong> in{" "}
              {city} and every surrounding {county} neighborhood. Complete
              flooring removal, subfloor prep, and baseboard finishing —
              handled by the same owner-operated crew from tear-out to trim.
            </p>

            <div className="mt-10 flex flex-wrap items-center gap-3">
              <a
                href="#contact"
                data-testid="city-cta-quote"
                className="inline-flex items-center gap-2 px-6 py-4 bg-orange-500 hover:bg-orange-600 text-stone-50 text-sm font-semibold rounded-full transition-colors group"
              >
                Get a Free {city} Quote
                <ArrowRight
                  size={16}
                  className="group-hover:translate-x-0.5 transition-transform"
                />
              </a>
              <a
                href="tel:+16368496681"
                data-testid="city-cta-call"
                className="inline-flex items-center gap-2 px-6 py-4 bg-stone-50/10 backdrop-blur-sm border border-stone-50/20 hover:bg-stone-50/20 text-stone-50 text-sm font-semibold rounded-full transition-colors"
              >
                <Phone size={15} /> 636-849-6681
              </a>
            </div>

            <div className="mt-14 grid grid-cols-2 md:grid-cols-4 gap-6 pt-10 border-t border-stone-800/60 max-w-3xl">
              {[
                { icon: Shield, label: "Licensed & Insured" },
                { icon: Star, label: "5.0 Google Rating" },
                { icon: MapPin, label: `${zips.length}+ ${city} ZIPs Served` },
                { icon: Check, label: "Free On-Site Quotes" },
              ].map((b) => {
                const Icon = b.icon;
                return (
                  <div
                    key={b.label}
                    className="flex items-center gap-3 text-stone-300"
                  >
                    <Icon
                      size={18}
                      className="text-orange-400 shrink-0"
                      strokeWidth={1.75}
                    />
                    <span className="text-sm font-medium">{b.label}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* LOCAL INTRO */}
        <section
          data-testid="city-intro"
          className="py-16 lg:py-24 bg-white border-b border-stone-200"
        >
          <div className="max-w-[1100px] mx-auto px-6 lg:px-10">
            <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.15em] text-orange-600 font-semibold mb-6">
              <span className="h-px w-8 bg-orange-600" />
              Your Local {city} Flooring Team
            </div>
            <h2 className="font-display font-semibold text-3xl md:text-4xl lg:text-5xl leading-[1.05] tracking-[-0.025em] text-stone-900 max-w-3xl">
              Trusted flooring installer in
              <br />
              <span className="text-stone-500">
                {city} &amp; surrounding {county}.
              </span>
            </h2>

            <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-8 text-[16px] leading-[1.75] text-stone-700">
              {localCopy.map((p, i) => (
                <p key={i}>{p}</p>
              ))}
            </div>

            {/* ZIP badges */}
            <div className="mt-10 flex flex-wrap gap-2">
              <span className="text-xs uppercase tracking-wider text-stone-500 font-semibold mr-2 self-center">
                {city} ZIPs served:
              </span>
              {zips.map((z) => (
                <span
                  key={z}
                  data-testid={`city-zip-${z}`}
                  className="inline-flex items-center gap-1 text-xs font-mono font-semibold text-stone-700 bg-stone-100 border border-stone-200 rounded-full px-3 py-1"
                >
                  {z}
                </span>
              ))}
            </div>
          </div>
        </section>

        {/* SERVICES */}
        <section
          id="services"
          data-testid="city-services"
          className="py-16 lg:py-24 bg-stone-50"
        >
          <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
            <div className="max-w-3xl mb-14">
              <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.15em] text-orange-600 font-semibold mb-6">
                <span className="h-px w-8 bg-orange-600" />
                Services in {city}
              </div>
              <h2 className="font-display font-semibold text-4xl md:text-5xl lg:text-6xl leading-[1.02] tracking-[-0.025em] text-stone-900">
                Complete flooring services
                <br />
                <span className="text-stone-500">
                  for every {city} home.
                </span>
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-7">
              {services.map((s) => (
                <article
                  key={s.title}
                  data-testid={`city-service-${s.title.toLowerCase().replace(/\s+/g, "-")}`}
                  className="group bg-white rounded-3xl overflow-hidden border border-stone-200 hover:border-stone-900/20 hover:-translate-y-1 hover:shadow-[0_20px_50px_-20px_rgba(28,25,23,0.18)] transition-all duration-500 flex flex-col"
                >
                  <div className="relative aspect-[4/3] overflow-hidden bg-stone-200">
                    <img
                      src={s.image}
                      alt={`${s.title} in ${city}, MO by M&L Exterior and Interior Design`}
                      loading="lazy"
                      className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1800ms] ease-out group-hover:scale-105"
                    />
                  </div>
                  <div className="p-6 lg:p-7 flex flex-col flex-1">
                    <h3 className="font-display font-semibold text-xl lg:text-2xl text-stone-900 tracking-tight leading-tight">
                      {s.title}
                    </h3>
                    <p className="mt-3 text-stone-600 leading-relaxed text-[15px] flex-1">
                      {s.copy}
                    </p>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* GALLERY */}
        <section
          data-testid="city-gallery"
          className="py-16 lg:py-24 bg-white"
        >
          <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
            <div className="max-w-3xl mb-12">
              <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.15em] text-orange-600 font-semibold mb-6">
                <span className="h-px w-8 bg-orange-600" />
                Recent {city} Installs
              </div>
              <h2 className="font-display font-semibold text-4xl md:text-5xl lg:text-6xl leading-[1.02] tracking-[-0.025em] text-stone-900">
                Flooring projects near
                <br />
                <span className="text-stone-500">{city}, MO.</span>
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6">
              {galleryImages.map((g, i) => (
                <figure
                  key={i}
                  data-testid={`city-gallery-${i}`}
                  className="group overflow-hidden rounded-2xl bg-stone-100 shadow-[0_10px_30px_-15px_rgba(0,0,0,0.15)]"
                >
                  <div className="aspect-[4/3] overflow-hidden">
                    <img
                      src={g.image}
                      alt={`${g.title} — flooring installation near ${city}, MO`}
                      loading="lazy"
                      className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-700"
                    />
                  </div>
                  <figcaption className="p-5">
                    <div className="font-display font-semibold text-lg text-stone-900 leading-tight">
                      {g.title}
                    </div>
                    <div className="mt-1 text-xs uppercase tracking-wider text-orange-600 font-semibold">
                      {g.type}
                    </div>
                  </figcaption>
                </figure>
              ))}
            </div>
          </div>
        </section>

        {/* NEIGHBORHOODS */}
        <section
          data-testid="city-neighborhoods"
          className="py-16 lg:py-24 bg-stone-900 text-stone-100 relative overflow-hidden"
        >
          <div
            aria-hidden
            className="absolute -top-32 -right-32 w-[600px] h-[600px] rounded-full opacity-30 blur-[140px] pointer-events-none"
            style={{
              background:
                "radial-gradient(circle, rgba(234,88,12,0.7) 0%, rgba(234,88,12,0) 70%)",
            }}
          />
          <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10">
            <div className="max-w-3xl mb-10">
              <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.15em] text-orange-400 font-semibold mb-6">
                <span className="h-px w-8 bg-orange-400" />
                {city} Neighborhoods
              </div>
              <h2 className="font-display font-semibold text-4xl md:text-5xl lg:text-6xl leading-[1.02] tracking-[-0.025em] text-stone-50">
                Serving every corner of
                <br />
                <span className="text-orange-400">{city}, MO.</span>
              </h2>
            </div>

            <ul className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-6 gap-y-3">
              {neighborhoods.map((n) => (
                <li
                  key={n}
                  data-testid={`city-nb-${n.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  className="flex items-center gap-2 text-stone-300"
                >
                  <span className="w-1.5 h-1.5 bg-orange-500 rounded-full shrink-0" />
                  {n}
                </li>
              ))}
            </ul>
          </div>
        </section>

        <Testimonials />

        {/* FAQ */}
        <section
          id="faq"
          data-testid="city-faq"
          className="py-16 lg:py-24 bg-stone-50"
        >
          <div className="max-w-[1000px] mx-auto px-6 lg:px-10">
            <div className="max-w-3xl mb-12">
              <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.15em] text-orange-600 font-semibold mb-6">
                <span className="h-px w-8 bg-orange-600" />
                {city} Flooring Questions
              </div>
              <h2 className="font-display font-semibold text-4xl md:text-5xl lg:text-6xl leading-[1.02] tracking-[-0.025em] text-stone-900">
                Flooring FAQs
                <br />
                <span className="text-stone-500">{city}, Missouri.</span>
              </h2>
            </div>

            <div className="divide-y divide-stone-200 border-y border-stone-200">
              {faqs.map((f, i) => (
                <details
                  key={i}
                  data-testid={`city-faq-${i}`}
                  className="group py-6"
                >
                  <summary className="flex items-start justify-between gap-6 cursor-pointer list-none">
                    <h3 className="font-display font-semibold text-lg lg:text-xl text-stone-900 leading-snug">
                      {f.q}
                    </h3>
                    <span className="shrink-0 w-8 h-8 rounded-full bg-stone-100 flex items-center justify-center text-stone-700 group-open:bg-orange-500 group-open:text-stone-50 transition-colors">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.5"
                        className="transition-transform group-open:rotate-45"
                      >
                        <line x1="12" y1="5" x2="12" y2="19" />
                        <line x1="5" y1="12" x2="19" y2="12" />
                      </svg>
                    </span>
                  </summary>
                  <p className="mt-4 text-stone-700 text-[15px] leading-[1.75] max-w-3xl">
                    {f.a}
                  </p>
                </details>
              ))}
            </div>
          </div>
        </section>

        <QuoteContact />
      </main>

      <Footer />
      <MobileStickyCTA />
    </div>
  );
}
